import React, { ReactElement, MouseEvent, useMemo } from 'react'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import CartReviewTileImage from './_CartReviewTileImage'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import NestableClickable from 'components/Common/NestableClickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

const Container = styled(NestableClickable)`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 200ms;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const CondensedDeleteButton = styled(IconButton)`
  border-color: transparent;
`

interface Props {
  imageElement?: ReactElement
  banner: React.ReactNode
  label?: ReactElement
  title: string
  dates?: string | false
  packageTitle?: string | Array<string>
  metaData?: ReactElement | false
  occupancy?: string | false
  roomName?: string
  price?: React.ReactNode
  hideTitleAndDates?: boolean
  to?: string
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void
}

function CondensedCartReviewTile({
  imageElement,
  banner,
  label,
  title,
  dates,
  packageTitle,
  metaData,
  occupancy,
  roomName,
  price,
  hideTitleAndDates = false,
  to,
  onDelete,
}: Props) {
  const packageTitles = useMemo<Array<string>>(() => {
    if (!packageTitle) return []
    if (Array.isArray(packageTitle)) {
      return packageTitle
    }
    return [packageTitle]
  }, [packageTitle])

  return (
    <Container to={to} target="_blank">
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
          <Group direction="horizontal" gap={16}>
            {imageElement && (
              <CartReviewTileImage
                imageElement={imageElement}
                label={label}
              />
            )}
            <Group direction="vertical">
              {!hideTitleAndDates && <Group direction="vertical" gap={4}>
                <Heading variant="heading6">{title}</Heading>
                <BodyText variant="medium" colour="neutral-two">{dates}</BodyText>
              </Group>}
              <Group direction="vertical" gap={2}>
                {roomName && <BodyText variant="medium" colour="neutral-two">{roomName}</BodyText>}
                {!roomName && packageTitles.map((pkgTitle) => <BodyText
                  key={pkgTitle}
                  variant="medium"
                  colour="neutral-two"
                  lineClamp={5}
                >
                  {pkgTitle}
                </BodyText>)}
                {metaData}
                {!!occupancy && <BodyText variant="medium" colour="neutral-two">{occupancy}</BodyText>}
              </Group>
              {price}
            </Group>
          </Group>
          <CondensedDeleteButton kind="secondary" onClick={onDelete}>
            <LineTrashAltIcon />
          </CondensedDeleteButton>
        </Group>
        {banner}
      </Group>
    </Container>
  )
}

export default CondensedCartReviewTile
